import {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {NatRule, PORT_PRESETS, Protocol} from '../../Types/routerForms';
import {IP_REGEX, newLocalId} from '../../Utils/random';

interface AddNatRuleModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (rule: NatRule) => void;
  publicIps: string[];
  privateNetworks: string[];
}

const isPortValid = (p: string): boolean => {
  if (!p) return false;
  const n = Number(p);
  return Number.isInteger(n) && n >= 0 && n <= 65535;
};

const AddNatRuleModal = ({
  show,
  onHide,
  onAdd,
  publicIps,
  privateNetworks,
}: AddNatRuleModalProps): JSX.Element => {
  const intl = useIntl();
  const [protocol, setProtocol] = useState<Protocol>('tcp');
  const [originIp, setOriginIp] = useState('');
  const [publicIp, setPublicIp] = useState(publicIps[0] ?? '');
  const [portPreset, setPortPreset] = useState('80');
  const [publicPort, setPublicPort] = useState('80');
  const [privateIp, setPrivateIp] = useState('');
  const [privatePort, setPrivatePort] = useState('80');
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setProtocol('tcp');
      setOriginIp('');
      setPublicIp(publicIps[0] ?? '');
      setPortPreset('80');
      setPublicPort('80');
      setPrivateIp('');
      setPrivatePort('80');
      setValidated(false);
    }
  }, [show, publicIps]);

  const isOriginValid = originIp === '' || IP_REGEX.test(originIp.split('/')[0]);
  const isPrivateIpValid = IP_REGEX.test(privateIp);

  const handlePortPresetChange = (value: string) => {
    setPortPreset(value);
    if (value !== 'custom') {
      setPublicPort(value);
      setPrivatePort(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (
      !isOriginValid ||
      !isPrivateIpValid ||
      !isPortValid(publicPort) ||
      !isPortValid(privatePort)
    )
      return;
    onAdd({
      id: newLocalId('nat'),
      protocol,
      originIp,
      publicIp,
      publicPort,
      privateIp,
      privatePort,
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ADD_RULE'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="addNatRuleForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PROTOCOL'})}
            </Form.Label>
            <Form.Select value={protocol} onChange={(e) => setProtocol(e.target.value as Protocol)}>
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ORIGIN_IP'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={originIp}
              onChange={(e) => setOriginIp(e.target.value)}
              placeholder="(optionnel — toutes)"
              isInvalid={validated && !isOriginValid}
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC_IP'})}
            </Form.Label>
            <Form.Select value={publicIp} onChange={(e) => setPublicIp(e.target.value)} required>
              {publicIps.map((ip) => (
                <option key={ip} value={ip}>
                  {ip}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.SERVICE'})}
            </Form.Label>
            <Form.Select
              value={portPreset}
              onChange={(e) => handlePortPresetChange(e.target.value)}
            >
              {PORT_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label} ({p.value})
                </option>
              ))}
              <option value="custom">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.CUSTOM_PORT'})}
              </option>
            </Form.Select>
          </Form.Group>
          {portPreset === 'custom' && (
            <Form.Group className="mb-3">
              <Form.Label className="required">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC_PORT'})}
              </Form.Label>
              <Form.Control
                type="text"
                value={publicPort}
                onChange={(e) => setPublicPort(e.target.value)}
                isInvalid={validated && !isPortValid(publicPort)}
                required
              />
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PORT'})}
              </Form.Control.Feedback>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVATE_IP'})}
            </Form.Label>
            {privateNetworks.length > 1 ? (
              <Form.Select
                value={privateIp}
                onChange={(e) => setPrivateIp(e.target.value)}
                required
              >
                <option value="">—</option>
                {privateNetworks.map((n) => (
                  <option key={n} value={n.split('/')[0]}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type="text"
                value={privateIp}
                onChange={(e) => setPrivateIp(e.target.value)}
                placeholder="10.0.0.50"
                isInvalid={validated && !isPrivateIpValid}
                required
              />
            )}
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVATE_PORT'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={privatePort}
              onChange={(e) => setPrivatePort(e.target.value)}
              isInvalid={validated && !isPortValid(privatePort)}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PORT'})}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="addNatRuleForm">
            {intl.formatMessage({id: 'COMMON.ADD'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddNatRuleModal;
